import { log } from "utils/log";

import { readMinimapFileOffline, writeMinimapFileOffline } from "./offlineStore";
import { type TiledDrawingSource, type TileSetLevel, type TileSetPreview } from "./tileMath";

const l = log.miniMap.tiled;

/**
 * Dev override: a manifest URL here routes every drawing through that tile
 * set, e.g. localStorage.setItem("novoweb:tiledDrawingManifestUrl",
 * "/mock-files/sample-tiling-files/vortex-tiles/tile/manifest_1.json").
 * Dev builds only (same gating as `isDevForceObjectTreeV2`): in production a
 * single localStorage write must not be able to reroute every drawing
 * through an attacker-chosen manifest.
 */
const OVERRIDE_MANIFEST_URL_STORAGE_KEY = "novoweb:tiledDrawingManifestUrl";

function getOverrideManifestUrl(): string | null {
    if (!import.meta.env.DEV) {
        return null;
    }
    try {
        return localStorage.getItem(OVERRIDE_MANIFEST_URL_STORAGE_KEY);
    } catch {
        return null;
    }
}

/**
 * Known manifest locations (production uses `tile/manifest_{page}.json`).
 * Everything else is read from the manifest itself, so only this list
 * changes if the server naming moves again.
 */
const MANIFEST_PATH_PATTERNS = [
    /(?:^|\/)manifests\/\d+\.json$/, // manifests/{page}.json (current agreement)
    /(?:^|\/)tile\/manifest_\d+\.json$/, // tile/manifest_{page}.json (earlier proposal)
    /(?:^|\/)manifest_\d+\.json$/, // manifest_{page}.json (current parser sample output)
];

function matchManifestPath(path: string): RegExpMatchArray | null {
    for (const pattern of MANIFEST_PATH_PATTERNS) {
        const match = path.match(pattern);
        if (match) {
            return match;
        }
    }
    return null;
}

export type ParsedManifestUrl = {
    /**
     * Full URL of the manifest document. Absent when resolved purely from
     * OPFS while offline — the manifest is read from the scene directory
     * instead (see `resolveTiledDrawingSource`), and there is no live URL to
     * hand back until the next successful online resolution.
     */
    manifestUrl?: string;
    /** Tile set root that the manifest's relative paths resolve against. Absent alongside `manifestUrl`. */
    baseUrl?: string;
    /**
     * Scene-file placement id (`platform_file_instance_id`), present whenever
     * the tiled route resolved this (as opposed to a dev override / a
     * drawing URL that is itself a manifest). Threaded onto the resolved
     * `TiledDrawingSource` so the OPFS read-through (`offlineStore.ts`) can
     * key tile-set files by it.
     */
    platformFileInstanceId?: string;
    /**
     * Re-resolves a fresh tile set root when the current one stops working —
     * FAS signed prefixes expire, and a large-map session can outlive the
     * signature. `null` = could not refresh. Absent for plain/dev URLs,
     * which don't expire.
     */
    refreshBaseUrl?: () => Promise<string | null>;
};

/**
 * Split a manifest-pointing URL into manifest URL + tile set base. Supports
 * plain paths (`…/prefix/tile/manifest_1.json` → base `…/prefix`) and FAS
 * signed-prefix style (`…?sig&file=tile/manifest_1.json` → base without the
 * `file` param). Returns `null` for anything else (e.g. PDF URLs).
 */
export function parseTiledManifestUrl(drawingUrl: string | null | undefined): ParsedManifestUrl | null {
    if (!drawingUrl) {
        return null;
    }

    try {
        const isRelative = drawingUrl.startsWith("/");
        const url = new URL(drawingUrl, "https://placeholder.invalid");

        // FAS style: the relative path travels in the `file` query parameter.
        const fileParam = url.searchParams.get("file");
        if (fileParam && matchManifestPath(fileParam)) {
            const base = new URL(url.toString());
            base.searchParams.delete("file");
            return {
                manifestUrl: drawingUrl,
                baseUrl: isRelative ? `${base.pathname}${base.search}` : base.toString(),
            };
        }

        // Plain style: the manifest path is the tail of the pathname.
        const match = matchManifestPath(url.pathname);
        if (match) {
            const matchStart = url.pathname.length - match[0].length;
            // Strip the matched tail plus its leading slash (when present).
            const basePath = url.pathname.slice(0, matchStart).replace(/\/$/, "");
            return {
                manifestUrl: drawingUrl,
                baseUrl: isRelative ? basePath : `${url.origin}${basePath}`,
            };
        }
    } catch {
        // Unparseable URL — not a tile set.
    }
    return null;
}

/**
 * Resolve the manifest URL for a drawing: its own URL when it points at a
 * manifest, else the localStorage override (if set). `null` → legacy PDF
 * route.
 */
export function resolveTiledManifestUrl(drawingUrl: string | null | undefined): ParsedManifestUrl | null {
    const own = parseTiledManifestUrl(drawingUrl);
    if (own) {
        return own;
    }
    return parseTiledManifestUrl(getOverrideManifestUrl());
}

/**
 * URL of a file inside the tile set: FAS signed prefixes get the `file=<path>`
 * query parameter set (same contract and mechanism as
 * `createBimSignedFileUrl`); plain prefixes get a path join.
 */
export function buildTileSetFileUrl(baseUrl: string, relativePath: string): string {
    if (baseUrl.includes("?")) {
        const isRelative = baseUrl.startsWith("/");
        const url = new URL(baseUrl, "https://placeholder.invalid");
        url.searchParams.set("file", relativePath);
        return isRelative ? `${url.pathname}${url.search}${url.hash}` : url.toString();
    }
    return `${baseUrl.replace(/\/+$/, "")}/${relativePath}`;
}

type RawManifest = {
    version?: number;
    width?: number;
    height?: number;
    tileSize?: number;
    minLevel?: number;
    maxLevel?: number;
    levels?: { level?: number; width?: number; height?: number }[];
    format?: string;
    tileUrlTemplate?: string;
    previews?: { name?: string; path?: string; url?: string; width?: number; height?: number }[];
};

/**
 * Validate a fetched manifest document and derive the drawing's
 * {@link TiledDrawingSource}. Pure (exported for tests).
 */
export function parseTileSetManifest(raw: unknown, baseUrl: string, manifestUrl: string): TiledDrawingSource {
    const manifest = raw as RawManifest;
    const { width, height, tileSize, minLevel, maxLevel, tileUrlTemplate } = manifest;
    if (
        !isPositive(width) ||
        !isPositive(height) ||
        !isPositive(tileSize) ||
        !isLevel(minLevel) ||
        !isLevel(maxLevel) ||
        maxLevel < minLevel ||
        typeof tileUrlTemplate !== "string" ||
        !tileUrlTemplate.includes("{quadkey}")
    ) {
        throw new Error(`Invalid tile set manifest at ${manifestUrl}`);
    }

    const levels: TileSetLevel[] = (manifest.levels ?? [])
        .filter(
            (entry): entry is TileSetLevel =>
                isLevel(entry.level) && isPositive(entry.width) && isPositive(entry.height),
        )
        .sort((a, b) => a.level - b.level);
    for (let level = minLevel; level <= maxLevel; level++) {
        if (!levels.some((entry) => entry.level === level)) {
            throw new Error(`Tile set manifest at ${manifestUrl} is missing dimensions for level ${level}`);
        }
    }

    const previews: TileSetPreview[] = (manifest.previews ?? [])
        .flatMap((entry) => {
            // Early manifest drafts used `url` for the relative path.
            const path = entry.path ?? entry.url;
            return typeof entry.name === "string" &&
                typeof path === "string" &&
                isPositive(entry.width) &&
                isPositive(entry.height)
                ? [{ name: entry.name, path, width: entry.width, height: entry.height }]
                : [];
        })
        .sort((a, b) => Math.max(a.width, a.height) - Math.max(b.width, b.height));
    if (previews.length === 0) {
        throw new Error(`Tile set manifest at ${manifestUrl} contains no usable previews`);
    }

    return { baseUrl, width, height, tileSize, minLevel, maxLevel, levels, tileUrlTemplate, previews };
}

function isPositive(value: unknown): value is number {
    return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function isLevel(value: unknown): value is number {
    return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

/** Manifest path inside a tile set (`1` = page number; sheets are single-page today). */
export const TILE_MANIFEST_RELATIVE_PATH = "tile/manifest_1.json";

/**
 * Resolved tile sources keyed by `drawingData.url`. Lives outside Redux on
 * purpose: the slice keeps only serializable status/dimensions.
 */
const sourcesByDrawingUrl = new Map<string, TiledDrawingSource>();

/**
 * Resolved tile source for a drawing. A drawing only reaches
 * `AsyncStatus.Success` after registration, so consumers gating on the async
 * state always observe a registered source.
 */
export function getTiledSource(drawingUrl: string | null | undefined): TiledDrawingSource | undefined {
    return drawingUrl ? sourcesByDrawingUrl.get(drawingUrl) : undefined;
}

/**
 * Fetch + parse the manifest and register the source under the drawing URL.
 * Online, tees the manifest into OPFS when the drawing has a
 * `platformFileInstanceId` (best-effort — offline reads never depend on this
 * succeeding). Offline, `manifest.manifestUrl` is absent (see
 * `manifestResolver.ts`'s offline ref) and the manifest is read from OPFS
 * instead, so this makes no network request at all.
 */
export async function resolveTiledDrawingSource(
    drawingUrl: string,
    manifest: ParsedManifestUrl,
): Promise<TiledDrawingSource> {
    const cached = sourcesByDrawingUrl.get(drawingUrl);
    const sameSource = cached
        ? manifest.platformFileInstanceId
            ? cached.fileInstanceId === manifest.platformFileInstanceId
            : cached.baseUrl === manifest.baseUrl
        : false;
    if (cached && sameSource) {
        // The cached source may have been resolved offline (`baseUrl: ""`,
        // no refresher) before this resolve brought a live one -- upgrade it
        // in place rather than returning a source an OPFS-miss can't fetch
        // through (`fetchTileSetFile` would hit `buildTileSetFileUrl("", ...)`).
        // Mutated in place: the object is shared by caches/painters, so every
        // later fetch picks up the live URL too.
        if (manifest.baseUrl && cached.baseUrl !== manifest.baseUrl) {
            cached.baseUrl = manifest.baseUrl;
            if (manifest.refreshBaseUrl) {
                refreshersBySource.set(cached, manifest.refreshBaseUrl);
            }
        }
        return cached;
    }

    const rawManifest = manifest.manifestUrl
        ? await fetchManifestOnline(manifest.manifestUrl, manifest.platformFileInstanceId)
        : await fetchManifestOffline(manifest.platformFileInstanceId);

    const source = parseTileSetManifest(
        rawManifest,
        manifest.baseUrl ?? "",
        manifest.manifestUrl ?? `${TILE_MANIFEST_RELATIVE_PATH} (offline)`,
    );
    source.fileInstanceId = manifest.platformFileInstanceId;
    if (manifest.refreshBaseUrl) {
        refreshersBySource.set(source, manifest.refreshBaseUrl);
    }

    sourcesByDrawingUrl.set(drawingUrl, source);
    l.debug("Resolved tiled drawing source:", drawingUrl, source);
    return source;
}

async function fetchManifestOnline(manifestUrl: string, fileInstanceId: string | undefined): Promise<unknown> {
    const response = await fetch(manifestUrl);
    if (!response.ok) {
        throw new Error(`Failed to fetch tile set manifest: HTTP ${response.status} for ${manifestUrl}`);
    }
    const text = await response.text();
    if (fileInstanceId) {
        // Best-effort: a failed tee here just means this manifest isn't
        // cached yet, not that resolution failed.
        writeMinimapFileOffline(
            fileInstanceId,
            TILE_MANIFEST_RELATIVE_PATH,
            new TextEncoder().encode(text).buffer as ArrayBuffer,
        );
    }
    return JSON.parse(text);
}

async function fetchManifestOffline(fileInstanceId: string | undefined): Promise<unknown> {
    const buffer = fileInstanceId
        ? await readMinimapFileOffline(fileInstanceId, TILE_MANIFEST_RELATIVE_PATH)
        : undefined;
    if (!buffer) {
        throw new Error("Tile set manifest is not available offline");
    }
    return JSON.parse(new TextDecoder().decode(buffer));
}

/** Base-URL refreshers for sources resolved from expiring signed prefixes. */
const refreshersBySource = new WeakMap<TiledDrawingSource, () => Promise<string | null>>();
/** Dedupe: many tiles 401/403 at once when a signature expires — refresh once. */
const refreshesInFlight = new WeakMap<TiledDrawingSource, Promise<boolean>>();

/**
 * Swap the source's expired base URL for a freshly minted one (mutated in
 * place — the source object is shared by the caches and painters, so every
 * later fetch picks it up). Returns `false` when the source has no refresher
 * (plain URL) or the refresh failed.
 */
async function refreshTileSetBaseUrl(source: TiledDrawingSource): Promise<boolean> {
    const refresher = refreshersBySource.get(source);
    if (!refresher) {
        return false;
    }
    const inFlight = refreshesInFlight.get(source);
    if (inFlight) {
        return inFlight;
    }
    const refresh = (async () => {
        try {
            const fresh = await refresher();
            if (!fresh) {
                return false;
            }
            l.debug("Refreshed expired tile set base URL");
            source.baseUrl = fresh;
            return true;
        } catch {
            return false;
        } finally {
            refreshesInFlight.delete(source);
        }
    })();
    refreshesInFlight.set(source, refresh);
    return refresh;
}

/**
 * Fetch a file inside the tile set: OPFS first (RFD00000145's incremental
 * cache), network on a miss. When the signed prefix has expired (401/403 — a
 * session can outlive a FAS signature), re-resolves it once and retries, so
 * long-lived large maps don't silently degrade to permanent holes. This is
 * the single seam every preview/tile request flows through.
 *
 * Deliberately OPFS-first even while online, not just as an offline
 * fallback: cutting network fetches while browsing online is the actual
 * point of incremental caching (RFD00000145), not a side effect of it. See
 * `minimapFilePath`'s doc for why a tile set's identity (`fileInstanceId`,
 * with no `sceneVersionId`) is expected not to go stale under a cached path.
 */
export async function fetchTileSetFile(source: TiledDrawingSource, relativePath: string): Promise<Response> {
    const fileInstanceId = source.fileInstanceId;
    if (fileInstanceId) {
        const cached = await readMinimapFileOffline(fileInstanceId, relativePath);
        if (cached) {
            return new Response(cached, { status: 200 });
        }
    }

    if (!source.baseUrl && !(await refreshTileSetBaseUrl(source))) {
        // Not cached, and no live prefix to fetch from yet (e.g. this source
        // was resolved straight from an OPFS-cached manifest, see
        // `manifestResolver.ts`) -- try to mint one now, on this first actual
        // need for it. A failure here (offline, or the signed-URL endpoint
        // down) just fails this one tile: the caller (`TileBitmapCache` /
        // `fetchPreviewBitmap`) already treats a rejection like any other
        // fetch failure, so the tile simply doesn't paint this session.
        throw new Error(`No live tile set base URL available for ${relativePath}`);
    }

    const response = await fetch(buildTileSetFileUrl(source.baseUrl, relativePath));
    if ((response.status === 401 || response.status === 403) && (await refreshTileSetBaseUrl(source))) {
        return fetchTileSetFileOnline(source.baseUrl, relativePath, fileInstanceId);
    }
    teeTileSetFileToOffline(response, relativePath, fileInstanceId);
    return response;
}

/** The network-only half of {@link fetchTileSetFile}, reused by the 401/403 retry after a refresh. */
async function fetchTileSetFileOnline(
    baseUrl: string,
    relativePath: string,
    fileInstanceId: string | undefined,
): Promise<Response> {
    const response = await fetch(buildTileSetFileUrl(baseUrl, relativePath));
    teeTileSetFileToOffline(response, relativePath, fileInstanceId);
    return response;
}

/**
 * Best-effort tee, off a clone so the caller's own read of `response` is
 * unaffected: never blocks or fails the caller on a write failure.
 */
function teeTileSetFileToOffline(response: Response, relativePath: string, fileInstanceId: string | undefined): void {
    if (!response.ok || !fileInstanceId) {
        return;
    }
    void response
        .clone()
        .arrayBuffer()
        .then((buffer) => writeMinimapFileOffline(fileInstanceId, relativePath, buffer))
        .catch((error: unknown) => l.debug("Failed to tee tile-set file to OPFS:", relativePath, error));
}
